import { PRICING_SUMMARY, PRICING_PROMOTION } from "./content";
import { SERVICE_AREA, REFUND_SUMMARY } from "./pageFaqs";

export const FAQ_SEGMENTS = [
  {
    id: "general",
    title: "General & Platform",
    items: [
      { q: "What is MagicBolt AI?", a: "MagicBolt AI is an all-in-one AI automation platform for agencies. It automates repetitive marketing work - Google Business Profile, Facebook, Instagram, WhatsApp, WordPress, reviews, QR, menus, websites and more - across every client from a single dashboard, and can be fully white-labeled and resold as your own product." },
      { q: "Who is MagicBolt AI built for?", a: "It's built for marketing agencies, freelancers and resellers who manage multiple local business clients and want to deliver more services in less time, with the option to sell everything under their own brand." },
      { q: "Do I need technical or coding skills to use it?", a: "No. Connecting channels takes a few clicks with secure OAuth, automations come pre-built, and everything is managed from a visual dashboard. If you can use a social scheduler, you can run MagicBolt." },
      { q: "Which products and channels are included?", a: "GMB, Facebook, Instagram, WhatsApp API CRM and WordPress automation, plus MagicQR + review routing, digital menus, digital visiting cards, instant websites, AEO/GEO optimization, appointment scheduling, and a full agency toolkit (client billing, bulk senders, AI generation and more)." },
    ],
  },
  {
    id: "gmb",
    title: "Google Business Profile Automation",
    items: [
      { q: "How does AI review auto-reply work?", a: "MagicBolt reads each new Google review, detects whether it's positive or negative, and generates a contextual, on-brand response that it can post automatically or hold for your approval - so every reviewer gets a fast, human-quality reply." },
      { q: "Can I schedule GMB posts across multiple locations?", a: "Yes. You can plan and bulk-schedule local updates, offers and event posts to a single location or across many locations at once, keeping every listing active without manual effort." },
      { q: "Does it track my local search rankings?", a: "Yes. Grid rank tracking automatically monitors your target keywords' local rankings over time and reports the changes, so you can prove local SEO progress to clients." },
    ],
  },
  {
    id: "facebook",
    title: "Facebook Automation",
    items: [
      { q: "Can I cross-post from GMB to Facebook?", a: "Yes. Omnichannel cross-posting automatically publishes your GMB updates to connected Facebook company pages, so you never duplicate work between platforms." },
      { q: "What does the Messenger AI assistant do?", a: "It's a smart chatbot that handles routine inquiries 24/7 - opening hours, booking requests and common questions - capturing leads even when your team is offline." },
    ],
  },
  {
    id: "instagram",
    title: "Instagram Automation",
    items: [
      { q: "Can I schedule reels and posts in advance?", a: "Yes. Plan vertical videos and visual posts using a unified drag-and-drop calendar and let MagicBolt publish them automatically at the best times." },
      { q: "How does DM lead routing work?", a: "Instagram Direct Messages land in the same unified inbox as Messenger, so every enquiry is seen and answered and no potential customer slips through the cracks." },
    ],
  },
  {
    id: "wordpress",
    title: "WordPress Automation",
    items: [
      { q: "How does the review sync widget work?", a: "It automatically pulls your 4 and 5-star Google reviews and displays them dynamically on your WordPress site, building trust with fresh social proof - no manual updates." },
      { q: "What is content mirroring?", a: "Content mirroring turns your AI-generated GMB posts into SEO-friendly blog articles and publishes them automatically to your connected WordPress site." },
    ],
  },
  {
    id: "qr-nfc",
    title: "Smart QR & NFC",
    items: [
      { q: "Can I change a printed QR code's destination without reprinting it?", a: "Yes. Dynamic destination routing lets you update where a printed QR points in real time - from a digital menu to a special offer, for example - without ever reprinting the code or table tent." },
      { q: "What are NFC-enabled visiting cards?", a: "Digital visiting cards with NFC let business owners share contact details, social links and review requests instantly with a single tap on a client's smartphone." },
      { q: "What analytics do I get from QR scans?", a: "The QR scan analytics engine breaks down offline engagement - scan locations, device types and time-of-day metrics - so you can measure the exact ROI of physical marketing." },
    ],
  },
  {
    id: "reviews",
    title: "Reviews & Feedback (MagicQR Routing)",
    items: [
      { q: "How does smart sentiment interception protect my rating?", a: "When a customer scans the review QR, they first see a quick rating screen. 1–3 star ratings are routed to a private internal feedback form for service recovery, while 4–5 star ratings are sent to public platforms like Google, Zomato or TripAdvisor." },
      { q: "Can I route reviews across multiple platforms?", a: "Yes. Instead of sending everyone to Google, automated review distribution rotates requests to where you need help most - balancing ratings across Yelp, Trustpilot and industry-specific sites." },
    ],
  },
  {
    id: "menu-card",
    title: "Digital Menu & Visiting Card",
    items: [
      { q: "Can restaurants update their menus in real time?", a: "Yes. Owners can update pricing, add daily specials or mark items 'sold out' on their digital menus instantly from a mobile device - no developer needed." },
      { q: "How does visual upselling work?", a: "You can pin high-margin items or seasonal specials to the top of the digital menu with rich imagery to drive higher average order values." },
    ],
  },
  {
    id: "website",
    title: "Instant Website & Content",
    items: [
      { q: "How fast can I launch a local SEO website?", a: "The Website module generates fast, mobile-friendly landing pages in one click. They automatically sync with the business's GBP data, pulling in the latest 5-star reviews and operating hours." },
      { q: "Does the AI generate content automatically?", a: "Yes. The AI content pipeline doesn't just suggest - it autonomously generates hyper-local captions and promotional graphics to keep websites and social feeds active on autopilot." },
    ],
  },
  {
    id: "aeo-geo",
    title: "AEO + GEO Automation",
    items: [
      { q: "What are AEO and GEO automation?", a: "AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization) tune your clients' content and entities so they get surfaced by AI answer engines and generative search - not just traditional Google results." },
      { q: "Why does AEO/GEO matter for my clients now?", a: "More customers are discovering businesses through AI assistants and generative search. Optimizing for these engines ensures your clients stay visible as search behavior shifts." },
    ],
  },
  {
    id: "whatsapp",
    title: "WhatsApp API CRM",
    items: [
      { q: "What does WhatsApp API CRM do?", a: "It runs a business's WhatsApp from one place: bulk messages, broadcast campaigns, 24/7 auto replies, and a chatbot that shares services and pricing, captures leads and books appointments inside the chat." },
      { q: "Can it reply to customers automatically?", a: "Yes. Auto replies answer incoming chats around the clock, and the chatbot guides customers through menu buttons such as services, pricing and booking a demo." },
      { q: "Can I send a message to many customers at once?", a: "Yes. Bulk messages and broadcast campaigns send offers and updates to your contact lists, and you can track how each campaign performs." },
    ],
  },
  {
    id: "white-label",
    title: "White-Label & Agency",
    items: [
      { q: "What does the white-label plan include?", a: "You get all products bundled into one platform under your own brand - custom logo, colors and domain, client billing and seats, API access and a dedicated success manager. Clients only ever see you." },
      { q: "Is MagicBolt 100% cloud hosted?", a: "Yes. The platform is fully managed and cloud hosted with always-on infrastructure - there are no servers for you to set up or maintain." },
      { q: "Do you offer support?", a: "Yes. White-label partners get dedicated support and white-glove onboarding for both your team and your clients." },
      { q: "Can I control plans and features for each client?", a: "Yes. Plans & Features Control lets you create your own pricing tiers and toggle exactly which features and products each client or plan unlocks." },
    ],
  },
  {
    id: "billing",
    title: "Billing & Payments",
    items: [
      { q: "Does the platform support recurring billing?", a: "Yes. You can automate subscriptions and invoices, billing your clients monthly or yearly on autopilot." },
      { q: "Which payment gateways are supported?", a: "An integrated online payment gateway lets you accept cards and UPI securely, collect payments and reconcile them automatically." },
    ],
  },
  {
    id: "agency-tools",
    title: "Agency Tools",
    items: [
      { q: "Can I find new business leads on Google Maps?", a: "Yes. The platform helps you discover active local businesses on Google Maps and export them as ready-to-work prospect lists for outreach." },
      { q: "Can I send bulk email and WhatsApp campaigns, and generate AI media?", a: "Yes. Use the bulk email + WhatsApp sender to broadcast campaigns to segmented lists, and the built-in AI to generate on-brand copy, images and short videos." },
    ],
  },
  {
    id: "policies",
    title: "Service Area & Refunds",
    items: [
      { q: "Which countries does MagicBolt AI serve?", a: SERVICE_AREA },
      { q: "What is the refund policy?", a: REFUND_SUMMARY },
      { q: "Can I cancel my plan?", a: "Yes. You can cancel any time from your account dashboard or by contacting support. Cancelling stops future billing; it doesn't refund the current period unless it qualifies under the Refund Policy." },
    ],
  },
  {
    id: "pricing",
    title: "Pricing & Plans",
    items: [
      { q: "How is pricing structured?", a: `${PRICING_SUMMARY.structure} Unlimited plans - ${PRICING_SUMMARY.unlimited().join("; ")}. Channel Based - ${PRICING_SUMMARY.channel().join("; ")}. All prices are in INR and currently include a limited-time ${PRICING_PROMOTION.discountPercent}% discount.` },
      { q: "What does the renewal fee cover?", a: "It keeps your platform, white-label branding and included products active - premium support, software updates, cloud hosting, security patches, new feature releases and uptime monitoring. Unlimited plans have no per-channel or per-client charges; Channel Based plans are priced by the number of channels you connect." },
      { q: "Can I upgrade to a higher plan later?", a: "Yes. You can move up from Digital Software to AI Agency or AI SaaS Business as your needs grow - reach out and we'll get you upgraded." },
      { q: "Is there a free trial?", a: "Yes. Every paid plan starts with a no-risk free trial, and no credit card is required to explore the platform." },
    ],
  },
];
